import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, Params } from '@angular/router';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { CartStatusUpdate, ErrorMessage } from '../store/+state/stores.actions';
import { Subject } from 'rxjs';
import { LocalStorageService } from 'src/app/local-storage.service';
import { HelperService } from '../helper.service';
import { ORIGIN_TYPE } from '../store/store-checkout/checkout.service';
import { IntegrationInitiatePaymentResponse, IntegrationProvider, ReferenceType } from '../store/types/IntegrationPayment';

@Injectable()
export abstract class PaymentRedirectGuardBase implements OnDestroy {
  unsubscribe$: Subject<void> = new Subject<void>();
  baseUrl: string;
  subdomain: string;
  protected constructor(protected store: Store<any>,
    protected router: Router,
    protected helperService: HelperService,
    protected zone: NgZone,
    protected storageService: LocalStorageService) {
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    this.baseUrl = (state.url.includes('/capture/')) ? state.url.split('/payment-redirect')[0] : '';
    let referenceType = this.getReferenceType(route, state);
    if (route.params.provider && route.params.storeId && route.params.orderUuid && route.queryParams) {

      let provider = this.enumFromStringValue(IntegrationProvider, route.params.provider) as unknown as IntegrationProvider;
      if (provider) {
        this.fetchPaymentStatus(route.params.storeId, route.params.paymentId, route.params.orderUuid, referenceType);
        this.selectAndDispatchPaymentStoreState(route.params, referenceType);
        return true;
      }
    }

    this.navigateToHome();
    return true;
  }

  processNavigation(paymentStateStatus: string, routeParams: Params, referenceType: ReferenceType, paymentstate: any) {

    if (paymentStateStatus == this.getCompletePaymentSuccessStatus()) {
      this.handleCompletePaymentSuccess(routeParams, referenceType, paymentstate);
    } else if (paymentStateStatus == this.getCompletePaymentFailureStatus()) {
      this.handleCompletePaymentFailure(routeParams, referenceType, paymentstate);
    }
  }

  protected handleRedirection(isStandalonePayment: boolean, urlRedrection: string, paymentState: IntegrationInitiatePaymentResponse) {
    const isOrderMobileApp = paymentState?.originType == ORIGIN_TYPE.APP;

    if (isOrderMobileApp) {
      this.handleMobileRedirection(isStandalonePayment, urlRedrection, paymentState);
    }

    if (!isOrderMobileApp) {
      this.handleWebRedirection(isStandalonePayment, urlRedrection);
    }
  }

  private handleMobileRedirection(isStandalonePayment: boolean, urlRedrection: string, paymentState: IntegrationInitiatePaymentResponse) {
    if (isStandalonePayment) {
      const url = `${paymentState?.originDomain}://${window.location.hostname}${urlRedrection}`;
      console.log('Navigate to Mobile SA', url);
      window.location.href = url;
      return;
    }

    if (!isStandalonePayment) {
      const url = `${paymentState?.originDomain}://${window.location.hostname}${urlRedrection}`;
      console.log('Navigate to Mobile', url);
      window.location.href = url;
      return;
    }
  }

  private handleWebRedirection(isStandalonePayment: boolean, urlRedirection: string) {
    if (isStandalonePayment) {
      const url = `${urlRedirection}`
      console.log('Navigate to Web SA', url);
      this.router.navigateByUrl(url);
      return;
    }

    if (!isStandalonePayment) {
      const url = `${urlRedirection}`;
      console.log('Navigate to Web', url);
      this.router.navigateByUrl(url);
      return;
    }
  }

  public_url() {
    const url: string = this.router.url;
    return url.split('#')[0];
  }

  isOrderCapture(): boolean {
    return this.public_url().includes('/capture/');
  }

  navigateToHome(): void {
    this.router.navigateByUrl(this.baseUrl);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  navigateToStandalone(){

      const standaloneOrder: string = this.storageService.getSavedState('standalone-order');
      const standaloneLocation: string = this.storageService.getSavedState('standalone-location');
      const amount = this.storageService.getSavedState('sp-amount');
      let url = window.location.origin + '/standalone-payment';
      if (amount){
        url = url + `/amount/${amount}`;
      } else if (standaloneLocation) {
        url = url + `/l/${standaloneLocation}`;
      } else {
        url = url + `/orderToken/${standaloneOrder}`;
      }
      window.location.href = url;
  }

  fetchPaymentStatus(storeId: number, paymentId: string, orderId: string, referenceType: ReferenceType) {
    this.store.dispatch(this.getFetchPaymentAction(storeId, paymentId, orderId, referenceType));
  }

  handleCompletePaymentSuccess(routeParams: Params, referenceType: ReferenceType, paymentstate: any) {

    this.onCompletePaymentSuccess(referenceType, paymentstate);
    this.store.dispatch(new CartStatusUpdate('FINISHED_ONLINE_PAYMENT'));
    let requestJson = paymentstate?.requestJSON;
    if (referenceType?.toString() == "STANDALONE_PAYMENT") {
      this.storageService.setSavedState(Number(routeParams.storeId), 'storeId');
      this.storageService.setSavedState(routeParams.orderUuid, 'standalonePaymentId');
      const url = '/standalone-payment/standalone-payment-success';
      this.handleRedirection(true, url, requestJson);
    } else {
      this.baseUrl = this.helperService.generateBasePath(
        this.baseUrl,
        routeParams.storeId,
        requestJson?.originType,
        requestJson?.originDomain,
        requestJson?.storeAliasName
      );

      const thankYouPath = `${this.baseUrl}#thankyou/e/${routeParams.orderUuid}`;
      this.handleRedirection(false, thankYouPath, requestJson);
    }
  }

  handleCompletePaymentFailure(routeParams: Params, referenceType: ReferenceType, paymentstate: any) {

    this.onCompletePaymentFailure(referenceType, paymentstate);
    let requestJson = paymentstate?.requestJSON;
    if (requestJson.paymentStatus == 'CANCELLED') {
      if (referenceType == ReferenceType.STANDALONE_PAYMENT) {
        this.navigateToStandalone();
      } else {
        this.navigateToHome();
      }

    } else  {
      if (referenceType == ReferenceType.STANDALONE_PAYMENT) {

        this.storageService.setSavedState(Number(routeParams.storeId), 'storeId');
        this.storageService.setSavedState(routeParams.orderUuid, 'standalonePaymentId');

        const url = '/standalone-payment/standalone-payment-error';
        this.handleRedirection(true, url, requestJson);

      } else {
        this.baseUrl = this.helperService.generateBasePath(
          this.baseUrl,
          routeParams.storeId,
          requestJson?.originType,
          requestJson?.originDomain,
          requestJson?.storeAliasName
        );

        const url = this.baseUrl + `#paymentError`;
        this.handleRedirection(false, url, requestJson);
      }
    }
    this.store.dispatch(new ErrorMessage('public.checkout.errors.paymentFailed'));
  }

  enumFromStringValue<T> (enumType: { [s: string]: T}, value: string): T | undefined {

    if (value === undefined || value === null) {
      return undefined;
    }

    let enumValues = Object.values(enumType) as unknown as string[];
    for (let index = 0; index < enumValues.length; index++) {
      let enumValue = enumValues[index];
      if (enumValue.toUpperCase() === value.toUpperCase()) {
        return enumValue as unknown as T;
      }
    }

    return undefined;
  }

  protected abstract selectAndDispatchPaymentStoreState(routeParams: Params, referenceType: ReferenceType);
  protected abstract getFetchPaymentAction(storeId: number, paymentId: string, orderId: string, referenceType: ReferenceType): Action;
  protected abstract getReferenceType(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): ReferenceType;
  protected abstract getCompletePaymentSuccessStatus();
  protected abstract onCompletePaymentSuccess(referenceType: ReferenceType, paymentstate: any);
  protected abstract getCompletePaymentFailureStatus();
  protected abstract onCompletePaymentFailure(referenceType: ReferenceType, paymentstate: any);
}
